/** Polls reports for browser-started direct runs through the existing results operation. */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import type { VerifiedTestFeedConnectionScope } from '../ShopifyAppContext'
import type { ShopifyAppBridge } from '../shopifyApiFetch'
import { readStudyAuthoringResults } from './study-builder/studyAuthoringClient'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'

type RunReport = Awaited<ReturnType<typeof readStudyAuthoringResults>>
export type ShopifyStudyActivitySnapshot = {
  reportsByRunId: Record<string, RunReport>
  errorsByRunId: Record<string, string>
  /** Runs the server answered 404 for: the run or its test no longer exists for this store (for example a deleted test). */
  goneRunIds: string[]
}
const EMPTY_ACTIVITY_SNAPSHOT: ShopifyStudyActivitySnapshot = { reportsByRunId: {}, errorsByRunId: {}, goneRunIds: [] }

/** A 404 is the server's settled answer that this run cannot be read for this store, not a transient failure. */
function isGoneRunError(reason: unknown) {
  return typeof reason === 'object' && reason !== null && 'status' in reason && reason.status === 404
}

function getDocumentVisibility() {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}
function subscribeToDocumentVisibility(onStoreChange: () => void) {
  if (typeof document === 'undefined') return () => undefined
  document.addEventListener('visibilitychange', onStoreChange)
  return () => document.removeEventListener('visibilitychange', onStoreChange)
}

export async function fetchShopifyStudyActivitySnapshot({
  shopify,
  studyIdByRunId,
  previousReportsByRunId,
  signal,
}: {
  shopify: ShopifyAppBridge
  studyIdByRunId: Record<string, string>
  previousReportsByRunId: Record<string, RunReport>
  signal: AbortSignal
}): Promise<ShopifyStudyActivitySnapshot> {
  const reportsByRunId: Record<string, RunReport> = {}
  const errorsByRunId: Record<string, string> = {}
  const goneRunIds: string[] = []
  const entries = Object.entries(studyIdByRunId)
  for (let offset = 0; offset < entries.length; offset += 20) {
    const batch = entries.slice(offset, offset + 20)
    const results = await Promise.allSettled(
      batch.map(([runId, studyId]) => readStudyAuthoringResults(shopify, { studyId, runId }, signal)),
    )
    results.forEach((result, index) => {
      const [runId] = batch[index]!
      if (result.status === 'fulfilled') {
        reportsByRunId[runId] = result.value
        return
      }
      if (signal.aborted || (result.reason instanceof DOMException && result.reason.name === 'AbortError')) return
      if (isGoneRunError(result.reason)) {
        goneRunIds.push(runId)
        console.warn('Shopify test activity: run no longer exists for this store; tracking ends', { runId })
        return
      }
      errorsByRunId[runId] =
        result.reason instanceof Error ? result.reason.message : 'Test results could not be checked'
      if (previousReportsByRunId[runId]) reportsByRunId[runId] = previousReportsByRunId[runId]
      console.error('Unable to refresh Shopify test activity', { runId, error: result.reason })
    })
  }
  return { reportsByRunId, errorsByRunId, goneRunIds }
}

export function useShopifyStudyActivityQuery({
  shopify,
  scope,
  studyIdByRunId,
}: {
  shopify: ShopifyAppBridge
  scope: VerifiedTestFeedConnectionScope | null
  studyIdByRunId: Record<string, string>
}) {
  const queryClient = useQueryClient()
  const isDocumentVisible = useSyncExternalStore(subscribeToDocumentVisibility, getDocumentVisibility, () => true)
  const entries = Object.entries(studyIdByRunId).sort(([left], [right]) => left.localeCompare(right))
  const queryKey = ['shopify-direct-study-activity', scope?.shopDomain, scope?.orgId, scope?.connectionId, entries]
  const query = useQuery({
    queryKey,
    enabled: Boolean(scope) && entries.length > 0 && isDocumentVisible,
    queryFn: ({ signal }) =>
      fetchShopifyStudyActivitySnapshot({
        shopify,
        studyIdByRunId,
        signal,
        previousReportsByRunId: queryClient.getQueryData<ShopifyStudyActivitySnapshot>(queryKey)?.reportsByRunId ?? {},
      }),
    refetchInterval: isDocumentVisible && entries.length > 0 ? 15_000 : false,
    ...shopifyQueryPolicies.studyActivity,
    refetchIntervalInBackground: false,
  })
  return { ...query, queryKey, snapshot: query.data ?? EMPTY_ACTIVITY_SNAPSHOT }
}
